import React, { useState, useEffect } from 'react';
import { useGetThreatOverviewQuery, useGetIntelActivityQuery } from '@/lib/services/threatFeedsApi';

import GlobalThreatMap from '@/components/features/threat-feeds/GlobalThreatMap';
import AttackVolumeChart from '@/components/features/threat-feeds/AttackVolumeChart';
import KpiStatCard from '@/components/features/threat-feeds/KpiStatCard';
import InterceptedIntelTable from '@/components/features/threat-feeds/InterceptedIntelTable';
import AttackThreatActorsList from '@/components/features/threat-feeds/AttackThreatActorsList';
import TargetedSectorsChart from '@/components/features/threat-feeds/TargetedSectorsChart';

const ThreatFeedsOverview: React.FC = () => {
    // 1. Fetch the live dashboard data from your backend
    const { data, isLoading } = useGetThreatOverviewQuery();

    // 2. Timeline Toggle State
    const [timeWindow, setTimeWindow] = useState<7 | 30 | 90>(30);

    // 3. Fetch specific timeline data based on the toggle state
    // We skip the request if timeWindow is 30, because the main overview data already provides the 30-day default
    const { data: activityData, isFetching: isActivityFetching } = useGetIntelActivityQuery(timeWindow, {
        skip: timeWindow === 30
    });

    // 4. Determine which dataset to hand to the chart
    const chartData = timeWindow === 30
        ? data?.widgets?.attack_volume_timeline
        : activityData?.intel_activity_timeline;

    const [liveTime, setLiveTime] = useState('');

    // Live Clock Effect synced to UTC+2
    useEffect(() => {
        const updateClock = () => {
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
                timeZone: 'Africa/Kigali',
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                hour12: false
            };

            const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(now);
            const timeVals = Object.fromEntries(parts.map(p => [p.type, p.value]));

            setLiveTime(`${timeVals.year}-${timeVals.month}-${timeVals.day} ${timeVals.hour}:${timeVals.minute}:${timeVals.second}`);
        };

        updateClock();
        const intervalId = setInterval(updateClock, 1000);
        return () => clearInterval(intervalId);
    }, []);

    // Safely extract exact backend values
    const lastSyncString = data?.meta?.last_sync_utc ? `${data.meta.last_sync_utc} UTC` : 'AWAITING TELEMETRY...';
    const systemStatus = data?.status ? data.status.toUpperCase() : 'ELEVATED';

    // Dynamic color based on backend status
    const statusColorClass = systemStatus === 'SUCCESS' ? 'text-[#50A970]' : 'text-[#FF3333]';

    return (
        <div className="flex flex-col gap-6 p-8 min-h-screen bg-black text-white font-mono">

            {/* 1. Page Header */}
            <div className="flex justify-between items-end mb-2">
                <div>
                    <h1 className="text-[22px] font-bold text-white tracking-wide">Threats Feeds</h1>
                    <p className={`text-[10px] mt-1 font-semibold uppercase tracking-widest ${statusColorClass}`}>
                        System_Status: {isLoading ? 'SYNCING...' : systemStatus}
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                    <span>Last_Sync: {isLoading ? 'Fetching...' : lastSyncString}</span>
                    <span className="w-2 h-2 rounded-full bg-[#50A970] shadow-[0_0_8px_rgba(80,169,112,0.8)] animate-pulse"></span>
                </div>
            </div>

            {/* 2. Global Threat Heatmap (Full Width) */}
            <section className="w-full h-[400px] bg-[#0A0B0E] border border-[#1A1D24] rounded-xl overflow-hidden shadow-lg relative">
                <div className="absolute top-4 left-6 z-10 font-bold tracking-wide text-sm flex items-center gap-4">
                    Global Threat Heatmap
                </div>
                <div className="absolute top-4 right-6 z-10 text-[10px] text-slate-500 tracking-widest uppercase">
                    Live • {liveTime.split(' ')[1]} CAT
                </div>

                <GlobalThreatMap data={data?.widgets?.global_threat_heatmap} isLoading={isLoading} />

            </section>

            {/* 3. KPI Stats Row (4 Columns) */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiStatCard title="Total Events (24H)" value={data?.widgets?.kpis?.total_events?.value || "48"} trend="+1.86%" trendType="up" />
                <KpiStatCard title="Active Adversaries (30D)" value={data?.widgets?.kpis?.active_adversaries?.value || "247"} trend="-12.4%" trendType="down" />
                <KpiStatCard title="Critical IOCs" value={data?.widgets?.kpis?.critical_iocs?.value || "1,043"} trend="+1.86%" trendType="up" />
                <KpiStatCard title="Targeted Sectors" value={data?.widgets?.kpis?.targeted_sectors?.value || "14"} trend="-19.4%" trendType="down" />
            </section>

            {/* 4. Bottom Content Grid (2 Columns) */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Left Column: Tables & Lists */}
                <div className="flex flex-col gap-6">
                    {/* Active Intercepted Intel Table */}
                    <InterceptedIntelTable data={data?.widgets?.intercepted_intel} isLoading={isLoading} />

                    {/* Active Attack Threats Actors List */}
                    <AttackThreatActorsList data={data?.widgets?.attack_threats_actors} isLoading={isLoading} />
                </div>

                {/* Right Column: Visualizations */}
                <div className="flex flex-col gap-6">
                    {/* Attack Volume Chart */}
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-5 shadow-lg min-h-[250px] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-[13px] font-bold tracking-wide">Attack Volume</h2>
                            <div className="flex gap-4 text-[10px] font-bold text-slate-500">
                                <button
                                    onClick={() => setTimeWindow(7)}
                                    className={`transition-colors duration-200 ${timeWindow === 7 ? 'text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded' : 'hover:text-white'}`}
                                >
                                    7D
                                </button>
                                <button
                                    onClick={() => setTimeWindow(30)}
                                    className={`transition-colors duration-200 ${timeWindow === 30 ? 'text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded' : 'hover:text-white'}`}
                                >
                                    30D
                                </button>
                                <button
                                    onClick={() => setTimeWindow(90)}
                                    className={`transition-colors duration-200 ${timeWindow === 90 ? 'text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded' : 'hover:text-white'}`}
                                >
                                    90D
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 relative">
                            <AttackVolumeChart data={chartData} isLoading={isLoading || isActivityFetching} />
                        </div>
                    </div>

                    <TargetedSectorsChart
                        data={data?.widgets?.targeted_sectors}
                        isLoading={isLoading}
                    />
                </div>
            </section>
        </div>
    );
};

export default ThreatFeedsOverview;